'use client';

import moment, { Moment } from "moment"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import * as echarts from 'echarts'

import { categoricalColorSchemes } from "@nivo/colors"
import { useDarkMode } from "@/hooks/useDarkMode";
import { useIsMobile } from "@/hooks/isMobile";
import { Button } from "../ui/button";
import { TrimContext } from "@/contexts/trimContext";

function not<T extends any[]>(f: (...args: T) => boolean) {
  return (...args: T) => !f(...args);
}

export type ColorSchemeKey = keyof typeof categoricalColorSchemes;

export interface ChartDataPoint {
  x: Date;
  y: number | null;
}

export interface ChartSeries {
  id: string;
  data: ChartDataPoint[];
}

export interface TooltipConfig {
  seriesNameFormatter?: (name: string) => string;
  seriesValueFormatter?: (value: number) => string;
}

export interface LineChartProps {
  // Required
  data: ChartSeries[];

  // Optional configuration
  title?: string;
  description?: string;
  height?: string;
  showLegend?: boolean;
  
  // Y-axis configuration
  yAxisFormatter?: (value: number) => string;
  yAxisMax?: number;
  yAxisScale?: boolean;
  
  // Tooltip configuration
  tooltip?: TooltipConfig;
  
  // Styling
  colorScheme?: ColorSchemeKey;
  symbol?: 'circle' | 'emptyCircle' | 'rect' | 'roundRect' | 'triangle' | 'diamond' | 'pin' | 'arrow' | 'none';
  smooth?: boolean;
  lineWidth?: number;

  trim?: Moment
  showTotal?: boolean
}

const defaultProps: Partial<LineChartProps> = {
  height: 'h-full',
  showLegend: true,
  yAxisScale: true,
  colorScheme: 'tableau10',
  symbol: 'none',
  smooth: false,
  lineWidth: 2,
};

export function LineChart({
  data,
  title,
  description,
  height = defaultProps.height,
  showLegend = defaultProps.showLegend,
  yAxisFormatter,
  yAxisMax,
  yAxisScale = defaultProps.yAxisScale,
  tooltip,
  colorScheme = defaultProps.colorScheme,
  symbol = defaultProps.symbol,
  smooth = defaultProps.smooth,
  lineWidth = defaultProps.lineWidth,
  showTotal,
}: LineChartProps) {
  const [hovered, setHovered] = useState<string | undefined>(undefined)
  const [chartInstance, setChartInstance] = useState<echarts.ECharts>()
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set())
  const trim = useContext(TrimContext)
  const chartRef = useRef<HTMLDivElement>(null)
  const isDark = useDarkMode()
  const isMobile = useIsMobile()

  const colors = categoricalColorSchemes[colorScheme || 'tableau10'];

  let notSeries = ['day', 'month', 'date', 'block_date']
  const keys = useMemo(() => [...new Set(data.map(s => s.id))].filter(k => !notSeries.includes(k)), [data])

  const defaultTooltipFormatter = (
    params: any[]
  ) => {
    const date = params[0].axisValue;
    let result = `${moment(date).format('YYYY-MM-DD')}<br/>`;
    let resultGrid = `<div class='grid grid-cols-2 auto-cols-fr text-xs'>`
    resultGrid += `<div class='col-span-2'><span>${moment(date).format('YYYY-MM-DD')}</span></div>`
    
    for (let param of params) {
      const value = param.value[1] as number;
      const isHovered = param.seriesName === hovered;
      const style = isHovered ? 'font-weight: bold; font-size: 1.2em;' : '';
      const classes = isHovered? 'font-bold text-sm': '';
      
      const formattedName = tooltip?.seriesNameFormatter 
        ? tooltip.seriesNameFormatter(param.seriesName)
        : param.seriesName;
      const formattedValue = tooltip?.seriesValueFormatter
        ? tooltip.seriesValueFormatter(value)
        : value.toString();
      
      result += `<span class="max-w-40 truncate" style="margin-right: 3em; ${style}">${param.marker} ${formattedName}</span><strong style="float:right; ${style}">${formattedValue}</strong><br/>`;
      resultGrid += `<div class="${classes}">${param.marker} ${formattedName}</div><div class='text-right ${classes}'>${formattedValue}</div>`
    }

    if (showTotal) {
      const to_add = params.map((d: any, idx: number) => {
        if (hiddenSeries.has(d.seriesName)) return 0
        else return d.data[1]
      })
      const total = to_add.reduce((acc: number, v?: string) => acc + (Number(v) || 0), 0) as number
      const formattedTotal = tooltip?.seriesValueFormatter
          ? tooltip.seriesValueFormatter(total)
          : total.toString();
      result += `<span class="max-w-40 truncate" style="margin-right: 3em; font-weight: bold">Total</span><strong style="float:right; font-weight: bold">${formattedTotal}</strong><br/>`;
      resultGrid += `<div class="font-bold">Total</div><div class='text-right font-bold'>${formattedTotal}</div>`
    }
    
    return resultGrid;
  };

  useEffect(() => {
    const element = chartRef.current;
    if (!element) return;

    // Ensure element has dimensions before initializing
    const initChart = () => {
      const _chartInstance = echarts.init(element);
      setChartInstance(_chartInstance);

      // Handle window resize
      const handleResize = () => {
        _chartInstance.resize();
      };

      // Handle container resize using ResizeObserver
      const resizeObserver = new ResizeObserver(() => {
        _chartInstance.resize();
      });
      resizeObserver.observe(element);

      // Handle series hover
      const handleMouseOver = (params: any) => {
        if (params.componentType === 'series') {
          setHovered(params.seriesName);
        }
      };

      const handleMouseOut = () => {
        setHovered(undefined);
      };

      // Handle legend select
      const handleLegendSelect = (params: any) => {
        if (params.name) {
          setHiddenSeries(prev => {
            const next = new Set(prev);
            if (next.has(params.name)) {
              next.delete(params.name);
            } else {
              next.add(params.name);
            }
            return next;
          });
        }
      };

      window.addEventListener('resize', handleResize);
      _chartInstance.on('mouseover', handleMouseOver);
      _chartInstance.on('globalout', handleMouseOut);
      _chartInstance.on('legendselectchanged', handleLegendSelect);

      return () => {
        window.removeEventListener('resize', handleResize);
        resizeObserver.disconnect();
        _chartInstance.off('mouseover', handleMouseOver);
        _chartInstance.off('globalout', handleMouseOut);
        _chartInstance.off('legendselectchanged', handleLegendSelect);
        _chartInstance.dispose();
      };
    };

    // Start initialization
    const cleanup = initChart();

    return () => {
      cleanup?.();
    };
  }, [])

  useEffect(() => {
    if (!keys || keys.length === 0) return;
    const textColor = isDark?"#ccc":"#222"

    const yAxisConfig: any = {
      type: 'value',
      scale: yAxisScale,
      axisLabel: {
        formatter: yAxisFormatter ? (n: any) => yAxisFormatter(n) : undefined,
        color: textColor
      },
      axisLine: {
        lineStyle: {
          color: textColor
        }
      },
      axisTick: {
        lineStyle: {
          color: textColor
        }
      },
      min: () => {
        return Math.min(
          data.map(s => s.data).flat().filter(p => trim?p.x >= trim:true).map(p => p.y).filter(p => p !== null).sort().at(0)!,
          0
        )
      },
      max: () => {
        return data.map(s => s.data).flat().filter(p => trim?p.x >= trim:true).map(p => p.y).filter(p => p !== null).sort().at(-1)
      }
    };

    if (yAxisMax !== undefined) {
      yAxisConfig.max = yAxisMax;
    }

    const breaks = trim?[{
        start: 0,
        end: trim
    }]:undefined

    const dataZoom = [{
      type: 'slider',
      show: false,
      xAxisIndex: [0],
      startValue: trim??undefined,
      filterMode: 'none'
    }]

    chartInstance?.setOption({
      darkMode: isDark,
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '3%',
        containLabel: true
      },
      dataZoom,
      xAxis: {
        type: 'time',
        name: 'Date',
        //breaks,
        axisLabel: {
          color: textColor
        },
        axisLine: {
          lineStyle: {color: textColor}
        },
        axisTick: {
          lineStyle: {color: textColor}
        }
      },
      yAxis: yAxisConfig,
      series: keys.sort().map((key, idx) => ({
        type: 'line',
        data: hiddenSeries.has(key)?[]:data.filter(s => s.id === key).at(0)?.data.map(d => [d.x, d.y]),
        name: key,
        lineStyle: {
          width: lineWidth,
          opacity: hovered ? (hovered === key ? 1 : 0.3) : 1
        },
        symbol: symbol,
        color: colors[idx % colors.length],
        smooth: smooth,
        triggerLineEvent: true,
        connectNulls: true
      })),
      tooltip: {
        trigger: 'axis',
        formatter: defaultTooltipFormatter,
        className: 'text-fg bg-background xs:text-xs',
        backgroundColor: '',
        textStyle: {
          color: '',
        },
      },
      legend: {
        show: false
      }
    })
  }, [isDark, chartInstance, data, hovered, title, yAxisFormatter, yAxisMax, yAxisScale, tooltip, symbol, colors, smooth, lineWidth, trim, hiddenSeries, keys])

  const handleLegendClick = (key: string) => {
    setHiddenSeries(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    setHiddenSeries(prev => {
      // If all series are hidden, show all; otherwise hide all
      if (prev.size === keys.length) {
        return new Set();
      } else {
        return new Set(keys);
      }
    });
  };

  const textColor = isDark?"#ccc":"#222"

  return (
    <div className='flex flex-col h-full lg:flex-row'>
      <div className='flex flex-col grow'> 
        { /* title */ }
        <div className="flex flex-row">
          <span className="block text-2xl lg:ml-8 ml-4 grow">{title}</span>
          <span className="block text-xs place-self-end">{description}</span>
        </div>
        { /* chart */}
        <div className='w-full h-full' ref={chartRef} />
      </div>
      {/* legend */}
      {showLegend && (
        <div className='flex flex-col'>
        
          <div className={`flex hover:bg-primary/10 bg-muted rounded ${isMobile ? 'flex-row w-full' : 'flex-col w-[10vw] h-full ml-4'} gap-2 p-2 overflow-scroll`}>
            <button
              onClick={handleToggleAll}
              className={`flex gap-2 px-2 py-1 rounded text-sm transition-opacity font-medium ${
                hiddenSeries.size === keys.length ? 'opacity-100' : 'opacity-100'
              }`}
              style={{ color: textColor }}
            >
              <span className="truncate">
                {hiddenSeries.size === keys.length ? 'Show All' : 'Hide All'}
              </span>
            </button>
            {keys.sort().map((key, idx) => {
              const isHidden = hiddenSeries.has(key);
              return (
                <button
                  key={key}
                  onClick={() => handleLegendClick(key)}
                  onMouseEnter={() => setHovered(key)}
                  onMouseLeave={() => setHovered(undefined)}
                  className={`flex items-center gap-2 px-2 py-1 rounded text-sm transition-opacity ${
                    isHidden ? 'opacity-40' : 'opacity-100'
                  } ${hovered === key ? 'bg-primary/10' : ''}`}
                  style={{ color: textColor }}
                >
                  <span
                    className="w-3 h-3 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: colors[idx % colors.length] }}
                  />
                  <span className="truncate">{key.trim()}</span>
                </button>
              );
            })}
          </div>
      </div>)}

    </div>)
}
